import React, { useEffect, useState, useMemo } from 'react';
import { Dropdown, Modal } from 'react-bootstrap';
import { useRepeatingInvoice } from '../contexts/RepeatingInvoiceContext';
import { RepeatingInvoice } from '../../../types/accounting';
import { useIsMobile } from '../../../hooks/useIsMobile';
import CreateRepeatingInvoiceModal from './CreateRepeatingInvoiceModal';

type SortField = 'Contact' | 'Type' | 'Reference' | 'Frequency' | 'NextDate' | 'Total' | 'Status';

export function RepeatingInvoiceList() {
  const {
    repeatingInvoices,
    loading,
    actionLoading,
    fetchRepeatingInvoices,
    updateRepeatingInvoice,
    deleteRepeatingInvoice,
    manuallyGenerate,
  } = useRepeatingInvoice();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRepeating, setEditingRepeating] = useState<RepeatingInvoice | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<RepeatingInvoice | null>(null);
  // Confirmation modal for the [Generate now] one-off action.
  const [generateConfirm, setGenerateConfirm] = useState<RepeatingInvoice | null>(null);
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<'ALL' | 'ACTIVE' | 'PAUSED' | 'CANCELLED'>('ALL');
  const [filterType, setFilterType] = useState<'ALL' | 'invoice' | 'bill'>('ALL');
  const [search, setSearch] = useState('');
  const [searchApplied, setSearchApplied] = useState('');
  const [nextDateFrom, setNextDateFrom] = useState('');
  const [nextDateTo, setNextDateTo] = useState('');
  const [nextDateFromApplied, setNextDateFromApplied] = useState('');
  const [nextDateToApplied, setNextDateToApplied] = useState('');
  const [sortField, setSortField] = useState<SortField>('NextDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const isMobile = useIsMobile(768);

  useEffect(() => {
    const timer = setTimeout(() => setSearchApplied(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchRepeatingInvoices();
  }, [fetchRepeatingInvoices]);

  const filtered = useMemo(() => {
    let result = repeatingInvoices.filter((r) => {
      if (filterStatus !== 'ALL' && r.status !== filterStatus) return false;
      if (filterType !== 'ALL' && (r.templateType || 'invoice') !== filterType) return false;
      if (searchApplied) {
        const q = searchApplied.toLowerCase();
        let match =
          (r.contactName || '').toLowerCase().includes(q) ||
          (r.reference || '').toLowerCase().includes(q);
        // Line-item description search (added 2026-05) — matches the
        // same behaviour now in invoice + bill search.
        if (!match) {
          const lines = Array.isArray(r.lineItems) ? r.lineItems : [];
          for (const li of lines) {
            if ((li?.description || '').toLowerCase().includes(q)) {
              match = true;
              break;
            }
          }
        }
        if (!match) return false;
      }
      if (nextDateFromApplied || nextDateToApplied) {
        const nextDate = r.nextDate || '';
        if (nextDateFromApplied && nextDate < nextDateFromApplied) return false;
        if (nextDateToApplied && nextDate > nextDateToApplied) return false;
      }
      return true;
    });
    result = [...result].sort((a, b) => {
      let aVal: string | number = '';
      let bVal: string | number = '';
      switch (sortField) {
        case 'Contact':
          aVal = (a.contactName || '').toLowerCase();
          bVal = (b.contactName || '').toLowerCase();
          return sortDir === 'asc' ? (aVal as string).localeCompare(bVal as string) : (bVal as string).localeCompare(aVal as string);
        case 'Type':
          aVal = (a.templateType || 'invoice').toLowerCase();
          bVal = (b.templateType || 'invoice').toLowerCase();
          return sortDir === 'asc' ? (aVal as string).localeCompare(bVal as string) : (bVal as string).localeCompare(aVal as string);
        case 'Reference':
          aVal = (a.reference || '').toLowerCase();
          bVal = (b.reference || '').toLowerCase();
          return sortDir === 'asc' ? (aVal as string).localeCompare(bVal as string) : (bVal as string).localeCompare(aVal as string);
        case 'Frequency':
          aVal = a.frequency || '';
          bVal = b.frequency || '';
          return sortDir === 'asc' ? (aVal as string).localeCompare(bVal as string) : (bVal as string).localeCompare(aVal as string);
        case 'NextDate': {
          const aT = a.nextDate ? new Date(a.nextDate).getTime() : 0;
          const bT = b.nextDate ? new Date(b.nextDate).getTime() : 0;
          return sortDir === 'asc' ? aT - bT : bT - aT;
        }
        case 'Total': {
          const aTotal = (a.lineItems || []).reduce((s, li) => s + (li.quantity * li.unitAmount), 0);
          const bTotal = (b.lineItems || []).reduce((s, li) => s + (li.quantity * li.unitAmount), 0);
          return sortDir === 'asc' ? aTotal - bTotal : bTotal - aTotal;
        }
        case 'Status':
          aVal = (a.status || '').toLowerCase();
          bVal = (b.status || '').toLowerCase();
          return sortDir === 'asc' ? (aVal as string).localeCompare(bVal as string) : (bVal as string).localeCompare(aVal as string);
        default:
          return 0;
      }
    });
    return result;
  }, [repeatingInvoices, filterStatus, filterType, searchApplied, nextDateFromApplied, nextDateToApplied, sortField, sortDir]);

  const formatCurrency = (amount: number, currency = 'AUD') =>
    new Intl.NumberFormat('en-AU', { style: 'currency', currency }).format(amount);

  const formatDate = (d: string | undefined) => {
    if (!d) return '—';
    try { return new Date(d + 'T12:00:00Z').toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return d; }
  };

  const applyNextDateRange = () => { setNextDateFromApplied(nextDateFrom); setNextDateToApplied(nextDateTo); };
  const clearNextDateRange = () => { setNextDateFrom(''); setNextDateTo(''); setNextDateFromApplied(''); setNextDateToApplied(''); };
  const hasNextDateRange = !!nextDateFromApplied || !!nextDateToApplied;
  const hasActiveFilters = filterStatus !== 'ALL' || filterType !== 'ALL' || !!searchApplied || hasNextDateRange;

  const clearAllFilters = () => {
    setFilterStatus('ALL');
    setFilterType('ALL');
    setSearch(''); setSearchApplied('');
    setNextDateFrom(''); setNextDateTo('');
    setNextDateFromApplied(''); setNextDateToApplied('');
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortTh = ({ field, label }: { field: SortField; label: string }) => (
    <th className="min-w-100px cursor-pointer" onClick={() => handleSort(field)}>
      <div className="d-flex align-items-center">
        {label}
        {sortField === field && <i className={`bi bi-chevron-${sortDir === 'asc' ? 'up' : 'down'} ms-1`} />}
      </div>
    </th>
  );

  const frequencyLabel = (r: RepeatingInvoice) => {
    const labels: Record<string, string> = { WEEKLY: 'Weekly', MONTHLY: 'Monthly', QUARTERLY: 'Quarterly', YEARLY: 'Yearly' };
    return labels[r.frequency] || r.frequency;
  };

  const getTemplateTotal = (r: RepeatingInvoice) => {
    const amtType = r.lineAmountTypes || 'Exclusive';
    const subtotal = (r.lineItems || []).reduce((s, li) => {
      const line = (li.quantity ?? 1) * (li.unitAmount ?? 0);
      return s + line;
    }, 0);
    if (amtType === 'NoTax') return subtotal;
    if (amtType === 'Inclusive') return subtotal; // GST included, total is just the subtotal
    // Exclusive: add 10% GST on top
    const gst = (r.lineItems || []).reduce((s, li) => {
      if (li.taxType !== 'GST') return s;
      const line = (li.quantity ?? 1) * (li.unitAmount ?? 0);
      return s + Math.round(line * 0.1 * 100) / 100;
    }, 0);
    return subtotal + gst;
  };

  const statusBadge = (status: string) => {
    const map: Record<string, string> = { ACTIVE: 'badge-light-primary', PAUSED: 'badge-light-warning', CANCELLED: 'badge-light-secondary' };
    return map[status] || 'badge-light-secondary';
  };

  const handleDelete = async (r: RepeatingInvoice) => {
    setDeleteConfirm(null);
    try { await deleteRepeatingInvoice(r.id); } catch (err) { console.error('Failed to delete repeating invoice:', err); }
  };

  // Auto-email-draft is ON by default (absent === enabled).
  const autoEmailOn = (r: RepeatingInvoice) => r.autoEmailDraft !== false;

  // Toggle whether this template auto-drafts the client email when it generates.
  // onSnapshot reflects the change; no local state to sync.
  const handleToggleAutoEmail = async (r: RepeatingInvoice) => {
    try { await updateRepeatingInvoice(r.id, { autoEmailDraft: !autoEmailOn(r) }); }
    catch (err) { console.error('Failed to toggle auto-email:', err); }
  };

  // Confirm-then-generate. Stays in the modal during the call so the
  // user can see the spinner; closes on success after a short pause.
  // Doesn't advance the recurring schedule (backend behaviour).
  const handleGenerateNow = async (r: RepeatingInvoice) => {
    setGenerating(true);
    setGenerateError(null);
    try {
      const result = await manuallyGenerate(r.id);
      // Close the modal + flash a brief success label via state.
      setGenerateConfirm(null);
      // eslint-disable-next-line no-alert
      // Use a more graceful toast pattern if one exists in the project,
      // but fallback to alert here keeps it dependency-free. Many other
      // surfaces in this codebase already use alert + console for
      // accounting-side feedback.
      window.alert(`${result.type === 'bill' ? 'Bill' : 'Invoice'} ${result.number} created (dated ${result.date}).`);
    } catch (err: any) {
      console.error('[RepeatingInvoiceList] generate now failed:', err);
      setGenerateError(err?.message || 'Generate failed');
    } finally {
      setGenerating(false);
    }
  };

  if (loading && repeatingInvoices.length === 0) {
    return (
      <div className="card">
        <div className="card-body py-10 text-center">
          <span className="spinner-border text-primary" role="status" />
          <p className="text-muted mt-3">Loading recurring invoices...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Filters card */}
      <div className="card mb-4 mb-md-5">
        <div className="card-body py-3">
          {isMobile ? (
            <div className="accounting-filters-row accounting-filters-mobile d-flex align-items-center gap-2">
              <div className="d-flex align-items-center position-relative flex-grow-1 min-w-0">
                <i className="bi bi-search position-absolute ms-3 text-muted" />
                <input type="text" className="form-control form-control-sm ps-10 accounting-search-mobile" placeholder="Search recurring, descriptions..." value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
              <Dropdown align="end" className="d-flex">
                <Dropdown.Toggle variant="light" size="sm" className="accounting-filter-toggle-mobile d-flex align-items-center gap-2" id="recurring-filters-dropdown">
                  <i className="bi bi-sliders" />
                  <span className="d-none d-sm-inline">Filters</span>
                  {hasActiveFilters && <span className="badge bg-primary rounded-pill" style={{ fontSize: '0.65rem' }} />}
                </Dropdown.Toggle>
                <Dropdown.Menu className="accounting-filter-menu-mobile p-0" style={{ minWidth: 'min(320px, 100vw - 2rem)', maxHeight: '80vh', overflowY: 'auto' }}>
                  <div className="p-3">
                    <div className="dropdown-header text-uppercase fs-8 text-muted mb-2">Status</div>
                    <button type="button" className={`dropdown-item py-3 accounting-filter-touch ${filterStatus === 'ALL' ? 'active' : ''}`} onClick={() => setFilterStatus('ALL')}>All statuses</button>
                    <button type="button" className={`dropdown-item py-3 accounting-filter-touch ${filterStatus === 'ACTIVE' ? 'active' : ''}`} onClick={() => setFilterStatus('ACTIVE')}>Active</button>
                    <button type="button" className={`dropdown-item py-3 accounting-filter-touch ${filterStatus === 'PAUSED' ? 'active' : ''}`} onClick={() => setFilterStatus('PAUSED')}>Paused</button>
                    <button type="button" className={`dropdown-item py-3 accounting-filter-touch ${filterStatus === 'CANCELLED' ? 'active' : ''}`} onClick={() => setFilterStatus('CANCELLED')}>Cancelled</button>
                    <div className="dropdown-header text-uppercase fs-8 text-muted mt-3 mb-2">Type</div>
                    <button type="button" className={`dropdown-item py-3 accounting-filter-touch ${filterType === 'ALL' ? 'active' : ''}`} onClick={() => setFilterType('ALL')}><i className="bi bi-collection me-2" />All types</button>
                    <button type="button" className={`dropdown-item py-3 accounting-filter-touch ${filterType === 'invoice' ? 'active' : ''}`} onClick={() => setFilterType('invoice')}><i className="bi bi-receipt me-2 text-primary" />Invoices only</button>
                    <button type="button" className={`dropdown-item py-3 accounting-filter-touch ${filterType === 'bill' ? 'active' : ''}`} onClick={() => setFilterType('bill')}><i className="bi bi-cash-stack me-2 text-danger" />Bills only</button>
                    <div className="dropdown-header text-uppercase fs-8 text-muted mt-3 mb-2">Next date</div>
                    <div className="d-flex align-items-center gap-2 flex-wrap px-0 pb-2">
                      <input type="date" className="form-control form-control-sm accounting-input-touch" style={{ flex: '1 1 120px', minWidth: 0 }} value={nextDateFrom} onChange={(e) => setNextDateFrom(e.target.value)} title="From" />
                      <span className="text-muted small">to</span>
                      <input type="date" className="form-control form-control-sm accounting-input-touch" style={{ flex: '1 1 120px', minWidth: 0 }} value={nextDateTo} onChange={(e) => setNextDateTo(e.target.value)} title="To" />
                    </div>
                    <div className="d-flex gap-2">
                      <button type="button" className="btn btn-sm btn-primary flex-grow-1 accounting-btn-touch" onClick={applyNextDateRange} disabled={!nextDateFrom && !nextDateTo}>Apply</button>
                      {hasNextDateRange && <button type="button" className="btn btn-sm btn-light accounting-btn-touch" onClick={clearNextDateRange}>Clear</button>}
                    </div>
                    {hasActiveFilters && (
                      <button type="button" className="btn btn-sm btn-light w-100 mt-3 accounting-btn-touch" onClick={clearAllFilters}><i className="bi bi-x-lg me-2" /> Clear all filters</button>
                    )}
                    <div className="dropdown-header text-uppercase fs-8 text-muted mt-3 mb-2">Actions</div>
                    <button type="button" className="dropdown-item d-flex align-items-center py-3 accounting-filter-touch" onClick={() => fetchRepeatingInvoices()} disabled={!!actionLoading}>
                      {actionLoading ? <span className="spinner-border spinner-border-sm me-3" /> : <i className="bi bi-arrow-clockwise text-primary me-3" />}
                      {actionLoading ? 'Refreshing…' : 'Refresh Data'}
                    </button>
                  </div>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          ) : (
            <div className="accounting-filters-row d-flex align-items-center gap-3 flex-wrap">
              <div className="d-flex align-items-center position-relative flex-grow-1 accounting-filters-search" style={{ minWidth: '200px', maxWidth: '300px' }}>
                <i className="bi bi-search position-absolute ms-3 text-muted" />
                <input type="text" className="form-control form-control-sm ps-10" placeholder="Search recurring, descriptions..." value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
              <div className="dropdown">
                <button className="btn btn-sm btn-light dropdown-toggle d-flex align-items-center gap-2" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                  {filterStatus === 'ALL' ? 'All statuses' : filterStatus === 'ACTIVE' ? 'Active' : filterStatus === 'PAUSED' ? 'Paused' : 'Cancelled'}
                </button>
                <ul className="dropdown-menu py-2" style={{ minWidth: '160px' }}>
                  <li><button type="button" className="dropdown-item py-2" onClick={() => setFilterStatus('ALL')}>All statuses</button></li>
                  <li><button type="button" className="dropdown-item py-2" onClick={() => setFilterStatus('ACTIVE')}>Active</button></li>
                  <li><button type="button" className="dropdown-item py-2" onClick={() => setFilterStatus('PAUSED')}>Paused</button></li>
                  <li><button type="button" className="dropdown-item py-2" onClick={() => setFilterStatus('CANCELLED')}>Cancelled</button></li>
                </ul>
              </div>
              <div className="dropdown">
                <button className="btn btn-sm btn-light dropdown-toggle d-flex align-items-center gap-2" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                  <i className={`bi ${filterType === 'bill' ? 'bi-cash-stack' : filterType === 'invoice' ? 'bi-receipt' : 'bi-collection'} me-1`} />
                  {filterType === 'ALL' ? 'All types' : filterType === 'invoice' ? 'Invoices' : 'Bills'}
                </button>
                <ul className="dropdown-menu py-2" style={{ minWidth: '160px' }}>
                  <li><button type="button" className="dropdown-item py-2" onClick={() => setFilterType('ALL')}><i className="bi bi-collection me-2" />All types</button></li>
                  <li><button type="button" className="dropdown-item py-2" onClick={() => setFilterType('invoice')}><i className="bi bi-receipt me-2 text-primary" />Invoices</button></li>
                  <li><button type="button" className="dropdown-item py-2" onClick={() => setFilterType('bill')}><i className="bi bi-cash-stack me-2 text-danger" />Bills</button></li>
                </ul>
              </div>
              <div className="d-flex align-items-center gap-2">
                <input type="date" className="form-control form-control-sm" style={{ width: '140px' }} value={nextDateFrom} onChange={(e) => setNextDateFrom(e.target.value)} title="Next date from" />
                <span className="text-muted small">to</span>
                <input type="date" className="form-control form-control-sm" style={{ width: '140px' }} value={nextDateTo} onChange={(e) => setNextDateTo(e.target.value)} title="Next date to" />
                <button className="btn btn-sm btn-primary" onClick={applyNextDateRange} disabled={!nextDateFrom && !nextDateTo}>Apply</button>
                {hasNextDateRange && <button className="btn btn-sm btn-light" onClick={clearNextDateRange}>Clear</button>}
              </div>
              {hasActiveFilters && <button className="btn btn-sm btn-light" onClick={clearAllFilters}><i className="bi bi-x-lg me-1" /> Clear all</button>}
              <div className="ms-auto">
                <div className="dropdown">
                  <button className="btn btn-icon btn-light btn-sm" type="button" data-bs-toggle="dropdown" aria-expanded="false"><i className="bi bi-three-dots-vertical fs-4" /></button>
                  <ul className="dropdown-menu dropdown-menu-end py-2" style={{ minWidth: '200px' }}>
                    <li>
                      <button type="button" className="dropdown-item d-flex align-items-center py-2" onClick={() => fetchRepeatingInvoices()} disabled={!!actionLoading}>
                        {actionLoading ? <span className="spinner-border spinner-border-sm me-3" /> : <i className="bi bi-arrow-clockwise text-primary me-3" />}
                        {actionLoading ? 'Refreshing…' : 'Refresh Data'}
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* List card */}
      <div className="card">
        <div className="card-header border-0 pt-4 pt-md-5">
          <h3 className="card-title align-items-start flex-column">
            <span className="card-label fw-bold fs-3 mb-1">Recurring Invoices</span>
            <span className="text-muted mt-1 fw-semibold fs-7">
              {filtered.length} template{filtered.length !== 1 ? 's' : ''}
            </span>
          </h3>
          <div className="card-toolbar accounting-card-toolbar">
            <button className="btn btn-sm btn-success" onClick={() => { setEditingRepeating(null); setShowCreateModal(true); }}>
              <i className="bi bi-plus-lg me-2" />
              New recurring
            </button>
          </div>
        </div>
        <div className="card-body py-3">
          {filtered.length === 0 ? (
            <div className="text-center py-8 text-muted">
              <i className="bi bi-arrow-repeat fs-1" />
              <p className="mt-3 mb-0">No recurring invoices match the filters.</p>
              <button className="btn btn-sm btn-primary mt-3" onClick={() => setShowCreateModal(true)}>
                Create recurring invoice
              </button>
            </div>
          ) : (
            <>
              {isMobile && (
                <div className="d-md-none d-flex flex-column gap-2">
                  {filtered.map((r) => (
                    <div key={r.id} className="border rounded-3 p-3 bg-body recurring-mobile-card" style={{ transition: 'background-color 0.15s' }}>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <span className="fw-bold fs-6 text-gray-800">{r.contactName || '—'}</span>
                        <div className="d-flex gap-1">
                          <span className={`badge ${r.templateType === 'bill' ? 'badge-light-danger' : 'badge-light-primary'}`}>
                            {r.templateType === 'bill' ? 'Bill' : 'Invoice'}
                          </span>
                          <span className={`badge ${statusBadge(r.status)}`}>{r.status}</span>
                        </div>
                      </div>
                      <div className="text-muted fs-8 mb-1">{r.reference || '—'}</div>
                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <span className="badge badge-light-info">{frequencyLabel(r)}</span>
                        <span className="fw-semibold">{formatCurrency(getTemplateTotal(r))}</span>
                      </div>
                      <div className="fs-8 text-muted mt-1">
                        Next: {formatDate(r.nextDate)}
                      </div>
                      <div className="mt-2 pt-2 border-top d-flex justify-content-end gap-1 flex-wrap">
                        {r.templateType !== 'bill' && (
                          <button
                            type="button"
                            className={`btn btn-sm ${autoEmailOn(r) ? 'btn-light-success' : 'btn-light'}`}
                            onClick={() => handleToggleAutoEmail(r)}
                            disabled={r.status === 'CANCELLED' || !!actionLoading}
                            title={autoEmailOn(r) ? 'Auto-email ON — click to turn off' : 'Auto-email OFF — click to turn on'}
                          >
                            <i className={`bi ${autoEmailOn(r) ? 'bi-envelope-check' : 'bi-envelope-slash'} me-1`} />
                            {autoEmailOn(r) ? 'Auto-email' : 'Auto-email off'}
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-sm btn-light-success"
                          onClick={() => { setGenerateError(null); setGenerateConfirm(r); }}
                          disabled={r.status === 'CANCELLED' || !!actionLoading}
                          title="Generate now (one-off, dated today)"
                        >
                          <i className="bi bi-lightning-charge me-1" />Generate
                        </button>
                        <button type="button" className="btn btn-sm btn-light-primary" onClick={() => { setEditingRepeating(r); setShowCreateModal(true); }} disabled={r.status === 'CANCELLED' || !!actionLoading}>
                          <i className="bi bi-pencil me-1" />Edit
                        </button>
                        <button type="button" className="btn btn-sm btn-light-danger" onClick={() => setDeleteConfirm(r)} disabled={!!actionLoading}>
                          <i className="bi bi-trash" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className={`table-responsive ${isMobile ? 'd-none' : ''}`}>
                <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
                  <thead>
                    <tr className="fw-bold text-muted">
                      <SortTh field="Contact" label="Contact" />
                      <SortTh field="Type" label="Type" />
                      <SortTh field="Reference" label="Reference" />
                      <SortTh field="Frequency" label="Frequency" />
                      <SortTh field="NextDate" label="Next date" />
                      <SortTh field="Total" label="Amount" />
                      <SortTh field="Status" label="Status" />
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((r) => (
                      <tr key={r.id}>
                        <td className="fw-semibold">{r.contactName || '-'}</td>
                        <td>
                          <span className={`badge ${r.templateType === 'bill' ? 'badge-light-danger' : 'badge-light-primary'}`}>
                            <i className={`bi ${r.templateType === 'bill' ? 'bi-cash-stack' : 'bi-receipt'} me-1`} />
                            {r.templateType === 'bill' ? 'Bill' : 'Invoice'}
                          </span>
                        </td>
                        <td>{r.reference || '-'}</td>
                        <td>{frequencyLabel(r)}</td>
                        <td>{formatDate(r.nextDate)}</td>
                        <td>{formatCurrency(getTemplateTotal(r))}</td>
                        <td><span className={`badge ${statusBadge(r.status)}`}>{r.status}</span></td>
                        <td className="text-end">
                          <div className="d-flex justify-content-end gap-1">
                            {r.templateType !== 'bill' && (
                              <button
                                className={`btn btn-sm btn-icon ${autoEmailOn(r) ? 'btn-light-success' : 'btn-light'}`}
                                onClick={() => handleToggleAutoEmail(r)}
                                disabled={r.status === 'CANCELLED' || !!actionLoading}
                                title={autoEmailOn(r)
                                  ? 'Auto-email: ON — a client email draft (with the invoice attached) is prepared each time this generates. Click to turn OFF.'
                                  : 'Auto-email: OFF — no email draft is prepared for this template. Click to turn ON.'}
                              >
                                <i className={`bi ${autoEmailOn(r) ? 'bi-envelope-check' : 'bi-envelope-slash text-muted'}`} />
                              </button>
                            )}
                            <button
                              className="btn btn-sm btn-icon btn-light-success"
                              onClick={() => { setGenerateError(null); setGenerateConfirm(r); }}
                              disabled={r.status === 'CANCELLED' || !!actionLoading}
                              title="Generate now (one-off, dated today)"
                            >
                              <i className="bi bi-lightning-charge" />
                            </button>
                            <button className="btn btn-sm btn-icon btn-light-primary" onClick={() => { setEditingRepeating(r); setShowCreateModal(true); }} disabled={r.status === 'CANCELLED' || !!actionLoading} title="Edit">
                              <i className="bi bi-pencil" />
                            </button>
                            <button className="btn btn-sm btn-icon btn-light-danger" onClick={() => setDeleteConfirm(r)} disabled={!!actionLoading} title="Delete">
                              <i className="bi bi-trash" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      <CreateRepeatingInvoiceModal
        show={showCreateModal}
        onHide={() => { setShowCreateModal(false); setEditingRepeating(null); }}
        editRepeating={editingRepeating}
      />

      <Modal show={!!deleteConfirm} onHide={() => setDeleteConfirm(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete recurring invoice</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleteConfirm && (
            <p className="mb-0">
              Are you sure you want to delete this recurring template for {deleteConfirm.contactName}? This cannot be undone.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-light" onClick={() => setDeleteConfirm(null)}>Cancel</button>
          <button className="btn btn-danger" onClick={() => deleteConfirm && handleDelete(deleteConfirm)}>Delete</button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!generateConfirm} onHide={() => !generating && setGenerateConfirm(null)} centered>
        <Modal.Header closeButton={!generating}>
          <Modal.Title>
            <i className="bi bi-lightning-charge text-success me-2" />
            Generate {generateConfirm?.templateType === 'bill' ? 'bill' : 'invoice'} now
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {generateConfirm && (
            <>
              <p className="mb-3">
                This will create a new <strong>{generateConfirm.templateType === 'bill' ? 'bill' : 'invoice'}</strong>
                {' '}for <strong>{generateConfirm.contactName || '—'}</strong> dated today, using this template's line items.
              </p>
              <ul className="list-unstyled mb-3 ps-2 fs-7 text-muted">
                <li>· Frequency: {frequencyLabel(generateConfirm)}</li>
                <li>· Reference: {generateConfirm.reference || '—'}</li>
                <li>· Total: {formatCurrency(getTemplateTotal(generateConfirm))}</li>
              </ul>
              <div className="alert alert-light-info py-2 mb-0 fs-8">
                <i className="bi bi-info-circle me-2" />
                One-off generation: the recurring schedule is unchanged. Your next scheduled
                {' '}{generateConfirm.templateType === 'bill' ? 'bill' : 'invoice'} will still go out on
                {' '}{formatDate(generateConfirm.nextDate)}.
              </div>
              {generateError && (
                <div className="alert alert-danger py-2 mt-3 mb-0 fs-7">
                  <i className="bi bi-exclamation-triangle me-2" />
                  {generateError}
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-light" onClick={() => setGenerateConfirm(null)} disabled={generating}>
            Cancel
          </button>
          <button
            className="btn btn-success"
            onClick={() => generateConfirm && handleGenerateNow(generateConfirm)}
            disabled={generating || !generateConfirm}
          >
            {generating
              ? (<><span className="spinner-border spinner-border-sm me-2" />Generating…</>)
              : (<><i className="bi bi-lightning-charge me-2" />Generate now</>)}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default RepeatingInvoiceList;
